import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, IconArrowLeft, Spinner, cx } from '@timely/ui';
import {
    STATEMENT_LINE_WORDS, ageingWindowRows, bookRow, customerStandingWords,
    type AccountStatement as StatementFigures, type Customer, type DateFormatter, type MoneyFormatter,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { paths } from '../workspace/navigation';
import { useCustomerDetail } from '../book/useBook';
import { useFormatters } from '../book/useFormatters';
import { isTallyKey } from '../book/accountCode';
import { useAccountStatement } from '../receivables/AccountStatement';
import { useCompanyProfile, type CompanyProfile } from '../branding/useCompanyProfile';
import { useLogoUrl } from '../branding/useLogo';
import { useDocumentTitle } from '../lib/documentTitle';
import { exportStatementCsv, mayExportStatement } from '../receivables/statementAccess';

/**
 * The account's statement as a document for the customer: the company's own
 * letterhead, who it is for, the balance and how old it is, every open bill
 * oldest first and the credits — every figure to the paisa, adding up to the
 * balance in the books.
 *
 * Printed, or saved as a PDF, by the browser's own print dialog, which sets a
 * name in any script it is written in; a PDF drawn in the page would not join
 * Devanagari letters. The page is a sheet of paper on screen too, so what is
 * seen is what prints, in the light theme whatever the person's choice.
 *
 * It is an export (`statementAccess.ts`): without "Export reports" the
 * address says so and reads nothing. With it, it reads what the account page
 * reads (`customers_read`, `source_account_detail`, the company profile any
 * member may read), so it shows nothing the person could not already open,
 * and an account they may not open is "not available" here as there. No
 * payment details are printed: the company profile holds none.
 */
export function CustomerStatement() {
    const context = useActiveWorkspace();
    // Refused before anything is read: the right is checked here, not only by hiding the buttons that lead here.
    return mayExportStatement(context) ? <StatementPage /> : <ExportRefused />;
}

/** Without the right to export: what is needed and where to ask, and nothing about the account. */
export function ExportRefused() {
    const { workspace } = useActiveWorkspace();
    const { customerId = '' } = useParams();
    useDocumentTitle('Statement');
    return (
        <main className="min-h-dvh bg-bg grid place-items-center px-4">
            <div role="alert" className="max-w-md text-center">
                <h1 className="font-display stretch-title text-title font-semibold text-label tracking-heading leading-tight">Statements need “Export reports”</h1>
                <p className="mt-3 text-body text-label-2 leading-relaxed">
                    A customer’s statement is a file that leaves Timely, so it needs the right to export, as every export does.
                    The account’s bills are still on its page. Ask whoever manages roles in your company if you need to send statements.
                </p>
                <Link to={paths(workspace.slug).customer(customerId)} className="mt-6 inline-flex items-center gap-1.5 text-secondary font-semibold text-accent-text hover:underline">
                    <IconArrowLeft size={14} />Back to the account
                </Link>
            </div>
        </main>
    );
}

function StatementPage() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { customerId = '' } = useParams();
    const { result, loading } = useCustomerDetail(context, customerId);
    const company = useCompanyProfile(workspace.id);
    const tally = useAccountStatement(workspace.id, customerId);
    const { money, date } = useFormatters();
    const logo = useLogoUrl(workspace.logoPath ?? null);
    usePaper();

    const to = paths(workspace.slug);
    const customer = result?.status === 'ok' ? result.detail.customer : null;
    const fromTally = isTallyKey(customer?.sourceKey);
    const statement = fromTally ? tally.statement : null;
    // The suggested file name when it is saved as a PDF.
    useDocumentTitle(customer ? `${customer.company} statement ${statement?.asOf ?? ''}`.trim() : 'Statement');

    const waiting = loading || !result || company.loading || (fromTally && tally.loading);

    return (
        <div className="min-h-dvh bg-bg print:bg-card">
            {/* The page's own size and margins on paper; the frame and the toolbar never print. */}
            <style>{'@page { size: A4; margin: 12mm 12mm 14mm; }'}</style>
            <div className="print:hidden sticky top-0 z-10 border-b border-separator bg-card">
                <div className="mx-auto max-w-[210mm] px-4 py-2.5 flex items-center justify-between gap-x-4 gap-y-2 flex-wrap">
                    <Link to={customer ? to.customer(customer.id) : to.customers}
                        className="inline-flex items-center gap-1.5 text-secondary font-semibold text-accent-text hover:underline">
                        <IconArrowLeft size={14} />{customer ? 'Back to the account' : 'Back to customers'}
                    </Link>
                    {customer && !waiting && (
                        <div className="flex items-center gap-2 flex-wrap">
                            {statement && (
                                <Button size="sm" variant="secondary" onClick={() => exportStatementCsv(context, statement, customer.company)}>
                                    Excel (CSV)
                                </Button>
                            )}
                            <Button size="sm" variant="primary" onClick={() => window.print()}>Print or save as PDF</Button>
                        </div>
                    )}
                </div>
                {customer && !waiting && (
                    <p className="mx-auto max-w-[210mm] px-4 pb-2.5 text-caption text-label-3">
                        To keep it as a PDF, choose “Save as PDF” as the printer in the print dialog.
                    </p>
                )}
            </div>

            <main className="mx-auto max-w-[210mm] sm:my-6 print:my-0 bg-card sm:rounded-card sm:shadow-e1 print:shadow-none print:rounded-none px-5 py-6 sm:px-[14mm] sm:py-[12mm] print:p-0">
                {waiting || !result ? (
                    <div className="py-20 grid place-items-center" role="status"><span className="sr-only">Preparing the statement</span><Spinner className="w-6 h-6 text-accent-text" /></div>
                ) : result.status === 'not-available' ? (
                    <div role="alert" className="py-10 text-center">
                        <h1 className="text-title font-semibold text-label">That account is not available</h1>
                        <p className="mt-3 text-body text-label-2 max-w-md mx-auto">
                            It may belong to another company, it may not be one of yours to work, or it may no longer exist.
                        </p>
                    </div>
                ) : result.status === 'error' ? (
                    <p role="alert" className="py-10 text-center text-body text-dang">{result.message}</p>
                ) : (
                    <StatementDocument
                        customer={result.detail.customer} statement={statement} profile={company.profile}
                        companyName={workspace.name} logo={logo} money={money} date={date}
                    />
                )}
            </main>
        </div>
    );
}

/** The light theme while a statement is open: it is a sheet of paper, on screen as when printed. */
export function usePaper() {
    useEffect(() => {
        const root = document.documentElement;
        const before = root.getAttribute('data-theme');
        if (before === 'light') return;
        root.setAttribute('data-theme', 'light');
        return () => { if (before === null) root.removeAttribute('data-theme'); else root.setAttribute('data-theme', before); };
    }, []);
}

function StatementDocument({ customer, statement, profile, companyName, logo, money, date }: {
    customer: Customer;
    statement: StatementFigures | null;
    profile: CompanyProfile;
    companyName: string;
    logo: string | null;
    money: MoneyFormatter;
    date: DateFormatter;
}) {
    const { count, today } = useFormatters();
    const s = statement;
    // A sheet's account, or one from TallyPrime before its bills are stored: its balance, signed by its side.
    const balance = s ? s.totals.balance : customer.totalType === 'Cr' ? -customer.total : customer.total;
    const asOf = s?.asOf ?? null;
    const theirPlace = [customer.city, customer.state].filter(Boolean).join(', ');

    // The ageing: the company's windows where TallyPrime is the source, else the sheet's four bands.
    const windows = customer.ageing.windows;
    const row = bookRow(customer, today);
    const ages: { label: string; amount: number }[] = windows
        ? [...ageingWindowRows(windows), ...(windows.basis === 'due_date' || windows.notYetDue > 0 ? [{ label: 'Not yet due', amount: windows.notYetDue }] : [])]
        : [['1–45 days', row.overdue.a1], ['46–90 days', row.overdue.a2], ['91–135 days', row.overdue.a3], ['Over 135 days', row.overdue.a4]].map(([label, amount]) => ({ label: label as string, amount: amount as number }));
    const agesTitle = s ? 'How old the open bills are' : 'How old the balance is';

    return (
        <article aria-label={`Statement for ${customer.company}`} className="text-label">
            <header className="flex items-start justify-between gap-x-8 gap-y-5 flex-wrap">
                <Letterhead profile={profile} companyName={companyName} logo={logo} />
                <div className="sm:text-right">
                    <h1 className="font-display stretch-title text-title font-semibold tracking-title leading-tight">Outstanding statement</h1>
                    <p className="mt-1 text-secondary text-label-2">As on <span className="num">{date.day(asOf ?? today)}</span></p>
                    <p className="text-caption text-label-3">Prepared on <span className="num">{date.day(today)}</span></p>
                </div>
            </header>

            <div className="mt-8 grid gap-x-10 gap-y-6 sm:grid-cols-2 items-start">
                <section aria-label="For">
                    <p className="text-caption font-semibold uppercase tracking-caps text-label-3">To</p>
                    <p className="mt-1 text-subhead font-semibold break-words">{customer.company}</p>
                    <div className="text-secondary text-label-2 leading-snug">
                        {customer.address && <p className="break-words">{customer.address}</p>}
                        {theirPlace && <p>{theirPlace}</p>}
                        {customer.taxId && <p>{profile.taxIdLabel}: <span className="num">{customer.taxId}</span></p>}
                    </div>
                </section>
                <section aria-label="Summary">
                    <p className="text-caption font-semibold uppercase tracking-caps text-label-3">Summary</p>
                    <dl className="mt-1">
                        {/* How the balance is made up, where there are bills to make it up: a ledger without bills, or an account
                            holding only credits, is its balance alone, the credits listed below it. */}
                        {s?.billWise && (s.totals.billCount > 0 || s.unbilledDebit) && (
                            <>
                                <PaperLine label={`Open bills (${count(s.totals.billCount)})`} value={money.precise(s.totals.bills)} />
                                {s.unbilledDebit && <PaperLine label={STATEMENT_LINE_WORDS.owed_on_account} value={money.precise(s.unbilledDebit.amount)} />}
                                {s.totals.credits > 0 && <PaperLine label="Less credits" value={`− ${money.precise(s.totals.credits)}`} />}
                            </>
                        )}
                        <PaperLine strong label={balance < 0 ? 'Balance in your favour' : 'Balance due'} value={money.precise(Math.abs(balance))} />
                        {/* Past the due date, as the customer reads it: the company's grace before it chases is its own. */}
                        {s && s.totals.pastDue > 0 && (
                            <PaperLine label={`Of which overdue (${count(s.totals.pastDueCount)} ${s.totals.pastDueCount === 1 ? 'bill' : 'bills'})`} value={money.precise(s.totals.pastDue)} />
                        )}
                    </dl>
                </section>
            </div>

            {/* By age: TallyPrime's open bills (before the credits, which are in no window), or a sheet's balance by its
                own bands. On paper in one row, on a phone in two columns. */}
            {balance > 0 && ages.some(a => a.amount > 0) && (
                <section aria-label={agesTitle} className="mt-8 break-inside-avoid">
                    <h2 className="text-secondary font-semibold">{agesTitle}</h2>
                    <dl className="mt-2 grid grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(7rem,1fr))] print:grid-cols-[repeat(auto-fit,minmax(6rem,1fr))] gap-x-4 border-t border-separator">
                        {ages.map(a => (
                            <div key={a.label} className="py-1.5 border-b border-separator">
                                <dt className="text-caption font-semibold text-label-3">{a.label}</dt>
                                <dd className="mt-0.5 text-caption num text-label">{money.precise(a.amount)}</dd>
                            </div>
                        ))}
                    </dl>
                </section>
            )}

            {s && s.bills.length > 0 && (
                <section aria-label="Open bills" className="mt-8">
                    <h2 className="text-secondary font-semibold">Open bills, oldest first</h2>
                    <table className="mt-2 w-full border-collapse text-caption">
                        <thead>
                            <tr className="text-label-3">
                                <PaperTh className="w-8">#</PaperTh>
                                <PaperTh>Bill</PaperTh>
                                <PaperTh wide>Bill date</PaperTh>
                                <PaperTh wide>Due date</PaperTh>
                                <PaperTh wide>Status</PaperTh>
                                <PaperTh numeric>Amount due</PaperTh>
                            </tr>
                        </thead>
                        <tbody>
                            {s.bills.map((b, i) => (
                                <tr key={b.key} className="break-inside-avoid">
                                    <PaperTd className="text-label-3 num">{i + 1}</PaperTd>
                                    <PaperTd className="font-semibold break-words">
                                        {b.ref || 'No reference'}
                                        {/* A phone's screen has room for three columns; paper has room for all six. */}
                                        <span className="block sm:hidden print:hidden font-normal text-label-3">
                                            {[date.day(b.billDate), customerStandingWords(b.standing)].filter(Boolean).join(' · ')}
                                        </span>
                                    </PaperTd>
                                    <PaperTd wide className="num whitespace-nowrap">{date.day(b.billDate)}</PaperTd>
                                    <PaperTd wide className="num whitespace-nowrap">{b.dueDate ? date.day(b.dueDate) : ''}</PaperTd>
                                    <PaperTd wide className={cx('whitespace-nowrap', b.standing.kind === 'overdue' || b.standing.kind === 'grace' ? 'text-label' : 'text-label-3')}>{customerStandingWords(b.standing)}</PaperTd>
                                    <PaperTd numeric>{money.precise(b.pending)}</PaperTd>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {/* After the table, not in its footer: a printed table repeats its footer on every page, and this is the
                        whole statement's total, not a page's. */}
                    <PaperTotal label="Total of open bills" value={money.precise(s.totals.bills)} />
                </section>
            )}

            {s?.billWise && s.credits.length > 0 && (
                <section aria-label="Credits" className="mt-8 break-inside-avoid">
                    <h2 className="text-secondary font-semibold">Credits, against no bill yet</h2>
                    <table className="mt-2 w-full border-collapse text-caption">
                        <thead>
                            <tr className="text-label-3"><PaperTh>Credit</PaperTh><PaperTh>Date</PaperTh><PaperTh numeric>Amount</PaperTh></tr>
                        </thead>
                        <tbody>
                            {s.credits.map(c => (
                                <tr key={c.key} className="break-inside-avoid">
                                    <PaperTd className="font-semibold break-words">{c.ref || STATEMENT_LINE_WORDS[c.kind]}</PaperTd>
                                    <PaperTd className="num whitespace-nowrap">{c.date ? date.day(c.date) : ''}</PaperTd>
                                    <PaperTd numeric>− {money.precise(c.amount)}</PaperTd>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <PaperTotal label="Total of credits" value={`− ${money.precise(s.totals.credits)}`} />
                </section>
            )}

            <footer className="mt-10 pt-4 border-t border-separator text-caption text-label-2 leading-relaxed break-inside-avoid">
                {!s && <p>This statement gives the balance by age. Ask us if you need it bill by bill.</p>}
                <p>Figures as in our books on <span className="num">{date.day(asOf ?? today)}</span>. Please check this statement against your records and tell us of any difference.</p>
                {profile.footerNote && <p className="mt-2 whitespace-pre-line">{profile.footerNote}</p>}
            </footer>
        </article>
    );
}

/** The company's own letterhead: its logo, legal name, address, how to reach it and its tax IDs. Never payment details. */
export function Letterhead({ profile, companyName, logo }: { profile: CompanyProfile; companyName: string; logo: string | null }) {
    const place = [profile.city, profile.state, profile.postalCode].filter(Boolean).join(', ');
    const reach = [profile.contactPhone, profile.contactEmail].filter(Boolean).join(' · ');
    return (
        <div className="flex items-start gap-4 min-w-0">
            {logo && <img src={logo} alt="" className="h-14 w-auto max-w-32 object-contain flex-none" />}
            <div className="min-w-0 text-secondary text-label-2 leading-snug">
                <p className="text-heading font-semibold text-label">{profile.legalName || companyName}</p>
                {profile.address && <p className="mt-0.5 whitespace-pre-line">{profile.address}</p>}
                {place && <p>{place}</p>}
                {reach && <p>{reach}</p>}
                {profile.taxId && <p>{profile.taxIdLabel}: <span className="num">{profile.taxId}</span></p>}
                {profile.secondaryTaxId && <p>{profile.secondaryTaxIdLabel || 'Tax ID'}: <span className="num">{profile.secondaryTaxId}</span></p>}
            </div>
        </div>
    );
}

export const PaperLine = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
    <div className={cx('flex items-baseline justify-between gap-4 py-0.5', strong && 'my-1 py-1.5 border-y border-separator-strong')}>
        <dt className={cx('text-secondary', strong ? 'font-semibold text-label' : 'text-label-2')}>{label}</dt>
        <dd className={cx('num whitespace-nowrap', strong ? 'text-subhead font-semibold' : 'text-secondary')}>{value}</dd>
    </div>
);

/** `wide`: a column a phone's screen leaves out (its facts go under the bill's reference); paper always has it. */
export const PaperTh = ({ children, numeric, wide, className }: { children: React.ReactNode; numeric?: boolean; wide?: boolean; className?: string }) => (
    <th scope="col" className={cx('py-1.5 px-2 first:pl-0 last:pr-0 font-semibold border-b border-separator whitespace-nowrap', numeric ? 'text-right' : 'text-left',
        wide && 'hidden sm:table-cell print:table-cell', className)}>{children}</th>
);

export const PaperTd = ({ children, numeric, wide, className }: { children: React.ReactNode; numeric?: boolean; wide?: boolean; className?: string }) => (
    <td className={cx('py-1.5 px-2 first:pl-0 last:pr-0 border-b border-separator align-top', numeric && 'num text-right whitespace-nowrap',
        wide && 'hidden sm:table-cell print:table-cell', className)}>{children}</td>
);

export const PaperTotal = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-baseline justify-between gap-4 pt-2 text-caption font-semibold break-inside-avoid">
        <span>{label}</span>
        <span className="num whitespace-nowrap">{value}</span>
    </div>
);
